import { defineMessages, type IntlShape } from 'react-intl';
import type { CollectionImportChangeDTO } from '@/api/trafikportalen';
import {
  COLLECTION_TYPE_LABELS,
  isStudentCollectionType,
} from '@/helpers/collectionTypes';
import { formatDate } from '@/helpers/formatting';
import {
  getChildAndYouthFieldMappings,
  getStudentTravelFieldMappings,
} from './fieldsLegendData';
import type { StudentType } from '@/types/students';

export const DUPLICATE_NIN_IMPORT_FIELD =
  'duplicate national identity number in import';

export const importChangeKey = (
  change: Pick<
    CollectionImportChangeDTO,
    'action' | 'row_number' | 'collection_member_id' | 'traveller_id'
  >,
): string => {
  if (change.action === 'removed') {
    return `removed:${change.collection_member_id ?? change.traveller_id ?? ''}`;
  }
  return `row:${change.row_number}`;
};

const COMMON_FIELD_LABELS: [string, string][] = [
  ['identifier', 'Unik identifierare'],
  ['school', 'Skola'],
  ['guardian1', 'Vårdnadshavare 1'],
  ['guardian2', 'Vårdnadshavare 2'],
  ['delivery_address', 'Leveransadress'],
  [DUPLICATE_NIN_IMPORT_FIELD, 'Duplicerat personnummer'],
  ['duplicate email in import', 'Duplicerad e-postadress'],
];

const buildStudentFieldNameLookup = (
  studentType: StudentType,
  disableHalfProduct: boolean,
): Map<string, string> => {
  const map = new Map<string, string>(COMMON_FIELD_LABELS);
  const mappings =
    studentType === 'child-and-youth'
      ? getChildAndYouthFieldMappings(disableHalfProduct)
      : getStudentTravelFieldMappings(disableHalfProduct);
  for (const f of mappings) {
    map.set(f.apiField, f.column);
  }
  return map;
};

let mergedFieldNameLookupCache: Map<string, string> | null = null;

const getMergedFieldNameLookup = (): Map<string, string> => {
  if (!mergedFieldNameLookupCache) {
    const map = new Map<string, string>(COMMON_FIELD_LABELS);
    for (const f of getStudentTravelFieldMappings(false)) {
      map.set(f.apiField, f.column);
    }
    for (const f of getChildAndYouthFieldMappings(false)) {
      map.set(f.apiField, f.column);
    }
    mergedFieldNameLookupCache = map;
  }
  return mergedFieldNameLookupCache;
};

export interface FormatFieldNameOptions {
  studentType?: StudentType;
  disableHalfProduct?: boolean;
}

const knownErrorTranslations = defineMessages({
  'error.import.address_or_stop_required': {
    id: 'error.import.address_or_stop_required',
    defaultMessage: 'Either address or bus stop is required',
  },
  'error.import.nin_required': {
    id: 'error.import.nin_required',
    defaultMessage: 'National identity number is required',
  },
  'error.import.invalid_email': {
    id: 'error.import.invalid_email',
    defaultMessage: 'Invalid email address',
  },
  'error.import.invalid_zipcode': {
    id: 'error.import.invalid_zipcode',
    defaultMessage: 'Invalid zip code',
  },
  'error.import.delivery_address_required': {
    id: 'error.import.delivery_address_required',
    defaultMessage: 'Delivery address is required',
  },
  'error.import.student_in_another_school': {
    id: 'error.import.student_in_another_school',
    defaultMessage: 'Student is already enrolled at another organisation',
  },
  'error.import.student_in_another_school_collection': {
    id: 'error.import.student_in_another_school_collection',
    defaultMessage:
      'Student is already enrolled in {collection} at another organisation',
  },
  'error.import.student_pending_inactivation': {
    id: 'error.import.student_pending_inactivation',
    defaultMessage:
      'Student is scheduled for inactivation at another organisation and has not been inactivated yet',
  },
  'error.import.student_pending_inactivation_details': {
    id: 'error.import.student_pending_inactivation_details',
    defaultMessage:
      'Student is scheduled for inactivation in {collection} at another organisation from {date} and has not been inactivated yet',
  },
  'error.import.invalid_nin_format': {
    id: 'error.import.invalid_nin_format',
    defaultMessage: 'Must be in YYYYMMDD-XXXX format',
  },
  'error.required': {
    id: 'error.required',
    defaultMessage: 'Required',
  },
  'error.import.school_address_required': {
    id: 'error.import.school_address_required',
    defaultMessage: 'Address is required',
  },
});

type KnownErrorKey = keyof typeof knownErrorTranslations;

const KNOWN_ERROR_MESSAGES = [
  {
    errorMessage: 'either address or bus_stop is required',
    translationKey:
      'error.import.address_or_stop_required' satisfies KnownErrorKey,
  },
  {
    errorMessage: 'either national_identity_number or email is required',
    translationKey: 'error.import.nin_required' satisfies KnownErrorKey,
  },
  {
    errorMessage: 'must be a valid email address',
    translationKey: 'error.import.invalid_email' satisfies KnownErrorKey,
  },
  {
    errorMessage: 'must be 5 digits (e.g. 12345 or 123 45)',
    translationKey: 'error.import.invalid_zipcode' satisfies KnownErrorKey,
  },
  {
    errorMessage: 'delivery_address is required',
    translationKey:
      'error.import.delivery_address_required' satisfies KnownErrorKey,
  },
  {
    errorMessage: 'student is already enrolled at another school',
    translationKey:
      'error.import.student_in_another_school' satisfies KnownErrorKey,
  },
  {
    errorMessage:
      'student is scheduled for inactivation at another school; retry once it has been inactivated',
    translationKey:
      'error.import.student_pending_inactivation' satisfies KnownErrorKey,
  },
  {
    errorMessage: 'must be in YYYYMMDD-XXXX format',
    translationKey: 'error.import.invalid_nin_format' satisfies KnownErrorKey,
  },
  {
    errorMessage: 'required',
    translationKey: 'error.required' satisfies KnownErrorKey,
  },
] as const;

const checkForKnownTranslation = (
  message: string,
): KnownErrorKey | undefined => {
  return KNOWN_ERROR_MESSAGES.find((error) => error.errorMessage === message)
    ?.translationKey;
};

export interface ParsedError {
  field: string;
  translationKey?: string;
  message: string;
  collectionType?: string;
  inactivateAt?: string;
}

const PENDING_INACTIVATION_ERROR =
  'student is scheduled for inactivation at another school; retry once it has been inactivated';

const parseSchoolEnrollmentError = (
  message: string,
): ParsedError | undefined => {
  const enrolled = message.match(
    /student is already enrolled at another school(?: \(([a-z0-9-]+)\))?/,
  );
  if (enrolled) {
    return {
      field: 'Error',
      message: enrolled[0],
      translationKey: 'error.import.student_in_another_school',
      collectionType: enrolled[1],
    };
  }

  const scheduled = message.match(
    /student is scheduled for inactivation at another school from ([0-9T:+.-]+)(?: \(([a-z0-9-]+)\))?/,
  );
  if (scheduled) {
    return {
      field: 'Error',
      message: scheduled[0],
      translationKey: 'error.import.student_pending_inactivation',
      inactivateAt: scheduled[1],
      collectionType: scheduled[2],
    };
  }

  if (message.includes(PENDING_INACTIVATION_ERROR)) {
    return {
      field: 'Error',
      message: PENDING_INACTIVATION_ERROR,
      translationKey: 'error.import.student_pending_inactivation',
    };
  }

  return undefined;
};

export const parseErrorMessage = (errorMessage: string): ParsedError[] => {
  const schoolEnrollmentError = parseSchoolEnrollmentError(errorMessage);
  if (schoolEnrollmentError) {
    return [schoolEnrollmentError];
  }

  const errors: ParsedError[] = [];

  let cleanedMessage = errorMessage;
  const firstColonIndex = cleanedMessage.indexOf(':');
  if (firstColonIndex !== -1) {
    const afterFirstColon = cleanedMessage.substring(firstColonIndex + 1);
    const secondColonIndex = afterFirstColon.indexOf(':');
    if (secondColonIndex !== -1) {
      cleanedMessage = afterFirstColon.substring(secondColonIndex + 1).trim();
    }
  }

  const errorParts = cleanedMessage.split(';').map((part) => part.trim());

  for (const part of errorParts) {
    const colonIndex = part.indexOf(':');
    if (colonIndex === -1) {
      if (part) {
        errors.push({
          field: 'Error',
          message: part,
          translationKey: checkForKnownTranslation(part),
        });
      }
      continue;
    }

    const field = part.substring(0, colonIndex).trim();
    const message = part.substring(colonIndex + 1).trim();

    if (field && message) {
      errors.push({
        field,
        message,
        translationKey: checkForKnownTranslation(message),
      });
    }
  }

  return errors.length > 0
    ? errors
    : [{ field: 'Error', message: errorMessage }];
};

export const formatFieldName = (
  field: string,
  options?: FormatFieldNameOptions,
): string => {
  const lookup = options?.studentType
    ? buildStudentFieldNameLookup(
        options.studentType,
        options.disableHalfProduct ?? false,
      )
    : getMergedFieldNameLookup();
  return (
    lookup.get(field) ??
    field
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  );
};

export const isFieldRequired = (
  field: string,
  studentType: StudentType,
  disableHalfProduct = false,
): boolean => {
  const mappings =
    studentType === 'child-and-youth'
      ? getChildAndYouthFieldMappings(disableHalfProduct)
      : getStudentTravelFieldMappings(disableHalfProduct);
  return mappings.some((f) => f.apiField === field && f.required);
};

const collectionLabel = (intl: IntlShape, collectionType?: string) => {
  if (!collectionType) return undefined;
  if (isStudentCollectionType(collectionType)) {
    return intl.formatMessage(COLLECTION_TYPE_LABELS[collectionType]);
  }
  return collectionType;
};

export const translateErrorMessage = (
  intl: IntlShape,
  error: ParsedError,
): string => {
  if (
    error.field === 'school' &&
    error.translationKey === 'error.import.address_or_stop_required'
  ) {
    return intl.formatMessage(
      knownErrorTranslations['error.import.school_address_required'],
    );
  }
  const collection = collectionLabel(intl, error.collectionType);
  if (
    error.translationKey === 'error.import.student_in_another_school' &&
    collection
  ) {
    return intl.formatMessage(
      knownErrorTranslations[
        'error.import.student_in_another_school_collection'
      ],
      { collection },
    );
  }
  if (
    error.translationKey === 'error.import.student_pending_inactivation' &&
    error.inactivateAt &&
    collection
  ) {
    return intl.formatMessage(
      knownErrorTranslations[
        'error.import.student_pending_inactivation_details'
      ],
      { collection, date: formatDate(error.inactivateAt) },
    );
  }
  if (error.translationKey && error.translationKey in knownErrorTranslations) {
    return intl.formatMessage(
      knownErrorTranslations[error.translationKey as KnownErrorKey],
    );
  }
  return error.message;
};

export const flattenObject = (obj: unknown): Record<string, string> => {
  if (typeof obj !== 'object' || obj === null) {
    return {};
  }

  return Object.entries(obj).reduce(
    (acc, [key, value]) => {
      if (typeof value === 'object' && value !== null) {
        const nested = flattenObject(value);
        for (const [nestedKey, nestedValue] of Object.entries(nested)) {
          acc[`${key}.${nestedKey}`] = nestedValue;
        }
      } else {
        acc[key] = String(value);
      }
      return acc;
    },
    {} as Record<string, string>,
  );
};
